<template>
  <div class="ssl-page">
    <!-- ======= Header ======= -->
    <div class="ssl-header">
      <div class="ssl-header-left">
        <el-button
          size="small"
          icon="el-icon-arrow-left"
          @click="goBack"
        >返回</el-button>
        <span class="ssl-title">降雨蛇行圖</span>
        <span v-if="queryTimeLabel" class="ssl-time-label">
          查詢時間：{{ queryTimeLabel }}
        </span>
      </div>
      <div class="ssl-header-right">
        <span class="ssl-ctrl-label">測站：</span>
        <el-select
          v-model="currentStation"
          size="small"
          style="width:120px"
          @change="onStationChange"
        >
          <el-option
            v-for="r in rawRows"
            :key="r.station"
            :label="r.station"
            :value="r.station"
          />
        </el-select>

        <span class="ssl-ctrl-label">Event：</span>
        <el-select
          v-model="selectedEventIdx"
          size="small"
          style="width:320px"
          :loading="loading"
          no-data-text="無降雨事件"
        >
          <el-option
            v-for="(ev, idx) in eventList"
            :key="idx"
            :label="ev.label"
            :value="idx"
          />
        </el-select>

        <span class="ssl-ctrl-label">實際截止</span>
        <el-date-picker
          v-model="pickerActualTime"
          type="datetime"
          format="yyyy-MM-dd HH:mm"
          value-format="timestamp"
          placeholder="預設：現在"
          size="small"
          style="width:190px"
        />
        <el-button type="primary" size="small" @click="fetchData">查詢</el-button>

        <span class="ssl-ctrl-label" style="margin-left:8px">顯示表格</span>
        <el-switch v-model="showTable" />
      </div>
    </div>

    <!-- ======= Charts ======= -->
    <div v-loading="loading" class="ssl-charts">
      <div class="ssl-chart-wrap">
        <div class="ssl-chart-title">3小時平均雨量</div>
        <v-chart
          :options="snakeChartOptions"
          :auto-resize="true"
          style="width:100%;height:420px"
        />
      </div>
      <div class="ssl-chart-wrap">
        <div class="ssl-chart-title">每10分鐘雨量</div>
        <v-chart
          :options="barChartOptions"
          :auto-resize="true"
          style="width:100%;height:420px"
        />
      </div>
    </div>

    <!-- ======= Table ======= -->
    <div v-if="showTable" class="ssl-table-wrap">
      <el-button type="primary" size="small" @click="exportToExcel">
        Export to Excel
      </el-button>
      <el-table
        :data="tableSlice"
        border
        size="small"
        style="width:100%;margin-top:12px"
        max-height="400"
      >
        <el-table-column prop="TIMESTAMP"                  label="Date"              width="160" />
        <el-table-column prop="threeHourMeanRainFall"      label="3小時平均雨量"     width="140" />
        <el-table-column prop="oneDayAccumulatedRainFall"  label="24小時累積雨量"    width="140" />
        <el-table-column prop="threeHourRainFallIntercept" label="3小時平均雨量截距" width="150" />
        <el-table-column prop="oneDayRainFallIntercept"    label="24小時累積雨量截距" />
      </el-table>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { THSR_NTU_STORM_PROJECT_ID, THSR_III_STORM_PROJECT_ID } from '@/lib/base/project';

const THSR_RAIN_URL = 'https://api-thsr-main.geosupply.com.tw/';

const FACTOR         = 0.1404;
const WIN_3H         = 18;    // 3hr  × 6 records/hr
const WIN_24H        = 144;   // 24hr × 6 records/hr
const WIN_23H        = 138;   // 23hr × 6 records/hr
const WIN_2H         = 12;    //  2hr × 6 records/hr
const WIN_1H         = 6;     //  1hr × 6 records/hr（時累積雨量）
const RAIN_THRESHOLD = 4;           // mm，事件開始／持續門檻
// 事件結束門檻：連續 6 小時 hourAccumulatedRainFall < 4mm。
// 用實際經過時間計算，不用筆數，避免資料缺筆造成誤差
const EVENT_GAP_MS   = 6 * 3600000;

function computeAlertLevel(x, y) {
  if (x <= 0 && y <= 0) return 0;
  const i3h  = Math.sqrt(FACTOR * FACTOR * x * x + y * y);
  const i24h = i3h / FACTOR;
  if (i3h > 40  || i24h > 285) return 3;
  if (i3h > 25  || i24h > 190) return 2;
  return 1;
}

function buildInterpolation(last6, lastX, lastY, predictX, predictY) {
  const lerp = t => [lastX + t * (predictX - lastX), lastY + t * (predictY - lastY)];
  if (last6.length < 6) {
    return [1, 2, 3, 4, 5].map(i => lerp(i / 6));
  }
  const vals  = last6.map(p => p.predict_sum_24h);
  const diffs = [];
  for (let i = 1; i < vals.length; i++) diffs.push(Math.max(vals[i] - vals[i - 1], 0));
  const total  = diffs.reduce((a, b) => a + b, 0);
  const ratios = total === 0 ? Array(5).fill(0.2) : diffs.map(d => d / total);
  const points = [];
  let acc = 0;
  for (const r of ratios) {
    const mid = Math.max(0.001, Math.min(0.999, acc + r / 2));
    points.push(lerp(mid));
    acc += r;
  }
  return points;
}

// 警戒弧線資料（共用）
const alertCurve = [];
const wslCurve   = [];
for (let i = 0; i <= 90; i++) {
  const rad = (Math.PI / 180) * i;
  alertCurve.push([190 * Math.cos(rad), 25 * Math.sin(rad)]);
  wslCurve.push([285 * Math.cos(rad), 40 * Math.sin(rad)]);
}

const RAW_ROWS = [
  { city: '新北', station: 'TK018', startLotCode: 'C210', endLotCode: 'C210' },
  { city: '桃園', station: 'TK021', startLotCode: 'C210', endLotCode: 'C210' },
  { city: '桃園', station: 'TK022', startLotCode: 'C210', endLotCode: 'C210' },
  { city: '新竹', station: 'TK028', startLotCode: 'C210', endLotCode: 'C215' },
  { city: '新竹', station: 'TK029', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK030', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK031', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK032', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK064', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK068', startLotCode: 'C215', endLotCode: 'C215' },
  { city: '新竹', station: 'TK070', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK071', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK076', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK077', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK078', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK079', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK080', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK081', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK082', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK083', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK084', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK085', startLotCode: 'C220', endLotCode: 'C220' },
  { city: '新竹', station: 'TK086', startLotCode: 'C220', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK087', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK088', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK089', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK090', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK092', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK093', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK094', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK096', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK097', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK098', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK099', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK100', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK101', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK102', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK103', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK104', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK107', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK108', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK109', startLotCode: 'C230', endLotCode: 'C230' },
  { city: '苗栗', station: 'TK110', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK112', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK113', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK114', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK115', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK116', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK117', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK118', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK119', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK120', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK121', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK122', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK123', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK124', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK125', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK126', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK127', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK128', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK129', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK130', startLotCode: 'C240', endLotCode: 'C240' },
  { city: '苗栗', station: 'TK131', startLotCode: 'C240', endLotCode: 'C250' },
  { city: '台中', station: 'TK139', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '台中', station: 'TK140', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '台中', station: 'TK141', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '台中', station: 'TK144', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '台中', station: 'TK146', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '台中', station: 'TK147', startLotCode: 'C250', endLotCode: 'C250' },
  { city: '彰化', station: 'TK170', startLotCode: 'C250', endLotCode: 'C260' },
  { city: '彰化', station: 'TK171', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK172', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK173', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK180', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK181', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK182', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK183', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK184', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK185', startLotCode: 'C260', endLotCode: 'C260' },
  { city: '彰化', station: 'TK186', startLotCode: 'C260', endLotCode: 'C260' },
];

export default {
  props: {
    source: { type: String, default: 'iii' },
    stationName: { type: String, required: true },
  },

  data() {
    // 從 route query 帶入的截止時間（RainGrid 傳過來）
    const queryActualTime = this.$route && this.$route.query && this.$route.query.actualTime
      ? Number(this.$route.query.actualTime)
      : null;
    return {
      rawRows: RAW_ROWS,
      currentStation: this.stationName,
      nameToId: {},
      processedData: null,
      eventList: [],
      selectedEventIdx: 0,
      showTable: true,
      loading: false,
      pickerActualTime: queryActualTime,
      // 查詢的實際時間範圍（供 header 顯示）
      fetchStart: null,
      fetchEnd: null,
    };
  },

  computed: {
    activeProjectId() {
      return this.source === 'ntu' ? THSR_NTU_STORM_PROJECT_ID : THSR_III_STORM_PROJECT_ID;
    },

    queryTimeLabel() {
      if (!this.fetchStart || !this.fetchEnd) return '';
      const fmt = d => this.$moment(d).format('YYYY/MM/DD HH:mm');
      return `${fmt(this.fetchStart)} - ${fmt(this.fetchEnd)}`;
    },

    selectedEvent() {
      return this.eventList[this.selectedEventIdx] || null;
    },

    // 依 Event 篩選的 tableData 切片
    tableSlice() {
      if (!this.processedData) return [];
      const td = this.processedData.tableData;
      if (!this.selectedEvent) return td;
      return td.slice(this.selectedEvent.startRecord, this.selectedEvent.endRecord + 1);
    },

    // SnakeLine 散點圖
    snakeChartOptions() {
      const actual = this.selectedEvent && this.processedData
        ? this.processedData.tableData
          .slice(this.selectedEvent.startRecord, this.selectedEvent.endRecord + 1)
          .map(r => [r.oneDayAccumulatedRainFall, r.threeHourMeanRainFall, r.TIMESTAMP])
        : (this.processedData ? this.processedData.trajectory.actual : []);

      const traj = this.processedData ? this.processedData.trajectory : null;

      // 只有在選中的 event 結束時間距 actualNow（fetchEnd）在 2 小時內才顯示預測
      let showPrediction = !!(traj && traj.lastActual && traj.predicted);
      if (showPrediction && this.selectedEvent && this.processedData && this.fetchEnd) {
        const endRow = this.processedData.tableData[this.selectedEvent.endRecord];
        if (endRow) {
          const eventEndMs = this.$moment(endRow.TIMESTAMP, 'YYYY-MM-DD HH:mm').valueOf();
          if (this.fetchEnd.getTime() - eventEndMs > 2 * 3600000) showPrediction = false;
        }
      }

      const series = [
        {
          type: 'line', data: alertCurve, smooth: true, showSymbol: false,
          lineStyle: { color: '#f59e0b', width: 1.5 }, tooltip: { show: false },
        },
        {
          type: 'line', data: wslCurve, smooth: true, showSymbol: false,
          lineStyle: { color: '#ef4444', width: 1.5 }, tooltip: { show: false },
        },
        {
          type: 'line', name: '實際軌跡', data: actual,
          lineStyle: { color: '#3b82f6', width: 2 },
          itemStyle: { color: '#3b82f6' }, symbolSize: 4,
        },
      ];

      if (showPrediction) {
        series.push({
          type: 'line', data: [traj.lastActual, traj.predicted], showSymbol: false,
          lineStyle: { color: '#f97316', width: 2, type: 'dashed' },
          tooltip: { show: false },
        });
        series.push({
          type: 'scatter', name: '預測點', data: [traj.predicted],
          itemStyle: { color: '#ef4444' }, symbolSize: 10,
        });
      }

      return {
        xAxis: { type: 'value', min: 0, name: '24小時累積雨量', nameLocation: 'middle', nameGap: 28 },
        yAxis: { type: 'value', min: 0, name: '3小時平均雨量' },
        tooltip: {
          trigger: 'item',
          formatter(params) {
            if (!Array.isArray(params.data)) return '';
            const [x, y, time] = params.data;
            const header = time ? `${time}<br>` : '';
            return `${header}24hr累積: ${Number(x).toFixed(3)}<br>3hr平均: ${Number(y).toFixed(3)}`;
          },
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none',
              title: {
                zoom: this.$t('chart.toolbox.dataZoomZoom'),
                back: this.$t('chart.toolbox.dataZoomBack'),
              },
            },
            restore: {
              title: this.$t('chart.toolbox.restore'),
            },
            saveAsImage: {
              title: this.$t('chart.toolbox.saveAsImage'),
            },
          },
        },
        legend: { show: false },
        series,
        backgroundColor: '#ffffff',
      };
    },

    // 每10分鐘雨量 Bar chart
    barChartOptions() {
      const barData = this.selectedEvent && this.processedData
        ? this.processedData.barData
          .slice(this.selectedEvent.startRecord, this.selectedEvent.endRecord + 1)
        : (this.processedData ? this.processedData.barData : []);

      return {
        xAxis: {
          type: 'category',
          name: '時間',
          data: barData.map(r => r.time),
          axisLabel: {
            rotate: 40,
            fontSize: 10,
            // 資料多時每 N 筆顯示一個刻度，避免擠在一起
            interval: Math.max(0, Math.floor(barData.length / 12) - 1),
          },
        },
        yAxis: { min: 0, name: 'mm' },
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const p = params[0];
            return `${p.name}<br>雨量: ${p.value} mm`;
          },
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none',
              title: {
                zoom: this.$t('chart.toolbox.dataZoomZoom'),
                back: this.$t('chart.toolbox.dataZoomBack'),
              },
            },
            restore: {
              title: this.$t('chart.toolbox.restore'),
            },
            saveAsImage: {
              title: this.$t('chart.toolbox.saveAsImage'),
            },
          },
        },
        series: [{
          type: 'bar',
          data: barData.map(r => ({
            value: r.reading2,
            // itemStyle: { color: r.reading2 > 5 ? '#ef4444' : '#60a5fa' },
            itemStyle: { color: '#60a5fa' },
          })),
        }],
        backgroundColor: '#ffffff',
      };
    },
  },

  watch: {
    stationName(val) {
      this.currentStation = val;
      this.fetchData();
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    // 蛇行圖是另開分頁，沒有瀏覽紀錄可退，改依 URL 的 from 回到預估表 A / B（未帶或無法辨識時一律回 A）
    goBack() {
      const from = String(this.$route.query.from || '').toUpperCase();
      this.$router.push({
        name: from === 'B' ? 'storm-raingrid-ntu' : 'storm-raingrid-iii',
        // 帶回當初預估表傳來的截止時間（URL 原值），不受本頁「實際截止」修改影響；未帶則預估表顯示今天
        query: this.$route.query.actualTime ? { actualTime: this.$route.query.actualTime } : {},
      });
    },

    async onStationChange(station) {
      // 切換測站時更新路由（讓 URL 同步），fetchData 由 watch stationName 觸發
      const routeName = this.source === 'ntu'
        ? 'storm-station-snakeline-ntu'
        : 'storm-station-snakeline-iii';
      // 保留 query（from 等），避免切換測站後「返回」的目的地遺失
      this.$router.replace({
        name: routeName,
        params: { stationName: station },
        query: this.$route.query,
      });
      // 若路由沒變（相同站），直接 fetch
      if (station === this.stationName) this.fetchData();
    },

    async fetchData() {
      this.loading = true;
      this.processedData = null;
      this.eventList = [];
      this.selectedEventIdx = 0;
      try {
        const actualNow  = this.pickerActualTime ? new Date(this.pickerActualTime) : new Date();
        const predictNow = new Date(actualNow.getTime() + 1 * 3600000); // +1hr 預測

        const yesterday = new Date(actualNow);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);

        const fetchFrom = new Date(yesterday);
        fetchFrom.setDate(fetchFrom.getDate() - 1);

        this.fetchStart = yesterday;
        this.fetchEnd   = actualNow;

        // 1. 取儀器清單
        const { data: instrData } = await this.$axios({
          category: '', method: 'get',
          apiName: `getRainGrid/${this.activeProjectId}`,
          useUrl: THSR_RAIN_URL,
        });
        const nameToId = {};
        for (const area of instrData.area) {
          for (const slope of area.slope) {
            for (const gp of slope.grid_point) {
              nameToId[gp?.latestVersion?.name ?? gp?.name] = gp._id;
            }
          }
        }
        this.nameToId = nameToId;

        const instrumentId = nameToId[this.currentStation];
        if (!instrumentId) {
          this.$message.warning(`找不到測站 ${this.currentStation} 的儀器資料`);
          return;
        }

        // 2. 取 output 資料
        const { data: outputs } = await this.$axios({
          category: 'project', method: 'post',
          apiName: `${this.activeProjectId}/output`,
          data: {
            date: [fetchFrom.getTime(), Math.max(actualNow.getTime(), predictNow.getTime())],
            interval: 'all',
            instrument: [instrumentId],
          },
          useUrl: THSR_RAIN_URL,
        });

        const records = outputs
          .filter(r => r.instrument_id === instrumentId)
          .sort((a, b) => new Date(a.TIMESTAMP) - new Date(b.TIMESTAMP));

        // 3. 處理資料
        this.processedData = this.processStationRecords(records, actualNow, predictNow);

        // 4. 建立 Event 清單（從 tableData 找連續降雨區間）
        this.eventList = this.buildEventList(this.processedData.tableData);
        if (this.eventList.length > 0) this.selectedEventIdx = this.eventList.length - 1;

      } catch (e) {
        console.error(e);
        this.$message.error('資料載入失敗');
      } finally {
        this.loading = false;
      }
    },

    processStationRecords(records, actualTime, predictTime) {
      const w3h = [], w24h = [], w23h = [], w2h = [], w1h = [];
      let s3h = 0, s24h = 0, s23h = 0, s2h = 0, s1h = 0;

      const last6predict = [];
      let lastActual = null;
      const actualPoints = [];
      const barData   = [];
      const tableData = [];
      const predByOffsetHour = {};
      let lastPredictPoint = null;

      for (const rec of records) {
        const ts = new Date(rec.TIMESTAMP);
        const r2 = rec.reading2 || 0;
        const r3 = rec.reading3 || 0;

        w24h.push(r2); s24h += r2; if (w24h.length > WIN_24H) s24h -= w24h.shift();
        w3h.push(r2);  s3h  += r2; if (w3h.length  > WIN_3H)  s3h  -= w3h.shift();
        w23h.push(r2); s23h += r2; if (w23h.length > WIN_23H) s23h -= w23h.shift();
        w2h.push(r2);  s2h  += r2; if (w2h.length  > WIN_2H)  s2h  -= w2h.shift();
        w1h.push(r2);  s1h  += r2; if (w1h.length  > WIN_1H)  s1h  -= w1h.shift();

        const x  = s24h;
        const y  = s3h / 3;
        const px = r3 + s23h;
        const py = (r3 + s2h) / 3;

        if (ts <= actualTime) {
          last6predict.push({ predict_sum_24h: px });
          if (last6predict.length > 6) last6predict.shift();

          const threeHourRainFallIntercept = Math.sqrt(FACTOR * FACTOR * x * x + y * y);
          const oneDayRainFallIntercept    = threeHourRainFallIntercept / FACTOR;
          const timeLabel = this.$moment(ts).format('YYYY-MM-DD HH:mm');

          actualPoints.push([x, y, timeLabel]);
          barData.push({ time: timeLabel, reading2: r2 });
          tableData.push({
            TIMESTAMP: timeLabel,
            reading2: r2,
            hourAccumulatedRainFall:      Number(s1h.toFixed(4)),
            threeHourMeanRainFall:        Number(y.toFixed(4)),
            oneDayAccumulatedRainFall:    Number(x.toFixed(4)),
            threeHourRainFallIntercept:   Number(threeHourRainFallIntercept.toFixed(4)),
            oneDayRainFallIntercept:      Number(oneDayRainFallIntercept.toFixed(4)),
          });

          lastActual = { x, y, last6: [...last6predict], px, py };
        } else if (ts <= predictTime) {
          const offsetHour = Math.floor((ts.getTime() - actualTime.getTime()) / 3600000) + 1;
          predByOffsetHour[offsetHour] = { px, py };
        }
      }

      // 預測格子（最多 +1hr）
      if (lastActual) {
        let prevX = lastActual.x, prevY = lastActual.y, prevLast6 = lastActual.last6;
        for (let h = 1; h <= 1; h++) {
          let px2, py2;
          if (predByOffsetHour[h]) {
            ({ px: px2, py: py2 } = predByOffsetHour[h]);
          } else {
            px2 = lastActual.px;
            py2 = lastActual.py;
          }
          const interpPoints = buildInterpolation(prevLast6, prevX, prevY, px2, py2);
          const level = Math.max(
            computeAlertLevel(px2, py2),
            ...interpPoints.map(([ix, iy]) => computeAlertLevel(ix, iy)),
          );
          if (level >= 0) {
            const predictLabel = this.$moment(new Date(actualTime.getTime() + h * 3600000)).format('YYYY-MM-DD HH:mm');
            lastPredictPoint = [px2, py2, predictLabel];
          }
          prevX = px2; prevY = py2; prevLast6 = [];
        }
      }

      return {
        barData,
        tableData,
        trajectory: {
          actual:     actualPoints,
          lastActual: lastActual ? [lastActual.x, lastActual.y] : null,
          predicted:  lastPredictPoint,
        },
      };
    },

    /**
     * 從 tableData 找降雨事件，邏輯對齊真實雨量計的 RainEventChecker：
     *   開始：hourAccumulatedRainFall >= RAIN_THRESHOLD（4 mm）
     *   持續：事件一旦開始，後續每筆都納入
     *   結束：連續 6 小時（依實際時間差計算，非筆數）hourAccumulatedRainFall < 4 mm，
     *         結束點為造成關閉的那一筆（含緩降尾巴，不回退），與真實雨量計的 endTime 對齊
     */
    buildEventList(tableData) {
      if (!tableData || tableData.length === 0) return [];

      const events = [];
      let inEvent = false;
      let startIdx = 0;
      let gapStartTime = null; // 這波連續低雨量開始的時間戳

      for (let i = 0; i < tableData.length; i++) {
        const aboveThreshold = tableData[i].hourAccumulatedRainFall >= RAIN_THRESHOLD;
        const ts = this.$moment(tableData[i].TIMESTAMP, 'YYYY-MM-DD HH:mm').valueOf();

        if (aboveThreshold) {
          if (!inEvent) {
            inEvent  = true;
            startIdx = i;
          }
          gapStartTime = null;
        } else if (inEvent) {
          if (gapStartTime === null) gapStartTime = ts;
          if (ts - gapStartTime >= EVENT_GAP_MS) {
            events.push(this.makeEvent(tableData, startIdx, i));
            inEvent = false;
            gapStartTime = null;
          }
        }
      }
      // 資料結束時若仍在事件中，收尾
      if (inEvent) {
        events.push(this.makeEvent(tableData, startIdx, tableData.length - 1));
      }

      return events;
    },

    makeEvent(tableData, startRecord, endRecord) {
      const startTime = tableData[startRecord].TIMESTAMP;
      const endTime   = tableData[endRecord].TIMESTAMP;
      return {
        label: `${startTime} ~ ${endTime}`,
        startRecord,
        endRecord,
      };
    },

    exportToExcel() {
      const data = this.tableSlice;
      if (!data || data.length === 0) return;

      const headers = ['Date', '3小時平均雨量', '24小時累積雨量', '3小時平均雨量截距', '24小時累積雨量截距'];
      const rows = data.map(r => [
        r.TIMESTAMP,
        r.threeHourMeanRainFall,
        r.oneDayAccumulatedRainFall,
        r.threeHourRainFallIntercept,
        r.oneDayRainFallIntercept,
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SnakeLine');
      const date = this.$moment().format('YYYYMMDD');
      XLSX.writeFile(wb, `snakeline_${this.currentStation}_${date}.xlsx`);
    },
  },
};
</script>

<style scoped>
.ssl-page {
  padding: 16px;
  background: #fff;
  min-height: 100%;
}

/* Header */
.ssl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}
.ssl-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ssl-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ssl-title {
  font-size: 18px;
  font-weight: 700;
}
.ssl-time-label {
  font-size: 13px;
  color: #6b7280;
}
.ssl-ctrl-label {
  font-size: 13px;
  color: #374151;
  white-space: nowrap;
}

/* Charts */
.ssl-charts {
  display: flex;
  gap: 16px;
  min-height: 440px;
}
.ssl-chart-wrap {
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 8px;
}
.ssl-chart-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #374151;
}

/* Table */
.ssl-table-wrap {
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}
</style>
